import { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { LayoutDashboard, Car, Users2, Sparkles, Wallet, ClipboardList, Plus, Pencil, Trash2, CheckCircle2, XCircle, Clock, Briefcase, Wand2, Upload, Image as ImageIcon, Calculator, Sparkle, X, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CATEGORY_LABELS, CATEGORY_ORDER, PLAN_LABELS, STATUS_LABELS, currency, formatDate } from "@/lib/format";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { computePricing, hourlyRate } from "@/lib/pricing";

const emptyVehicle = {
  name: "", brand: "", category: "premium_sedan", description: "", image_url: "", gallery: [],
  // Core pricing (new engine)
  governing_monthly_price: 3000,
  price_weekly: 600,
  price_monthly: 2000,
  member_price_daily: 85,
  member_price_weekly: 510,
  member_price_monthly: 1600,
  member_pricing_mode: "fixed", // or "percent_off_standard"
  member_discount_percent: 15,
  min_days_non_member: 7,
  min_days_member: 1,
  // Per-week discount %
  week_1_discount_pct: 0,
  week_2_discount_pct: 15,
  week_3_discount_pct: 17,
  week_4_discount_pct: 0,
  extra_days_discount_pct: 0,
  // Optional
  price_hourly: "",
  price_weekend: "",
  member_price_weekend: "",
  price_rtl_3_monthly: "",
  price_rtl_6_monthly: "",
  price_rtl_12_monthly: "",
  deposit: 500, seats: 5, transmission: "Automatic", drivetrain: "AWD", fuel_type: "Gasoline", year: 2024,
  mileage: "Unlimited km", available: true, urgency_tags: "", features: "",
  ai_enrich: true,
};

const statusBadge = (s) => {
  if (s === "confirmed" || s === "completed") return { cls: "badge-availability", icon: CheckCircle2 };
  if (s === "rejected" || s === "cancelled") return { cls: "badge-danger", icon: XCircle };
  return { cls: "badge-warning", icon: Clock };
};

export default function AdminPortalPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [analytics, setAnalytics] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [businessApps, setBusinessApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // vehicle being edited or null
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [form, setForm] = useState(emptyVehicle);
  const [enrichingId, setEnrichingId] = useState(null);

  const activeTab = params.get("tab") || "dashboard";
  const setTab = (t) => { const next = new URLSearchParams(params); next.set("tab", t); setParams(next, { replace: true }); };

  const load = async () => {
    setLoading(true);
    try {
      const [a, v, b, u, m, bi] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/vehicles?limit=200`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/users`),
        axios.get(`${API}/memberships`),
        axios.get(`${API}/business/applications`),
      ]);
      setAnalytics(a.data);
      setVehicles(v.data || []);
      setBookings(b.data || []);
      setCustomers(u.data || []);
      setMemberships(m.data || []);
      setBusinessApps(bi.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const openNew = () => { setEditing(null); setForm(emptyVehicle); setShowVehicleModal(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      ...v,
      gallery: Array.isArray(v.gallery) ? v.gallery : [],
      governing_monthly_price: v.governing_monthly_price ?? 0,
      member_pricing_mode: v.member_pricing_mode || "fixed",
      member_discount_percent: v.member_discount_percent ?? 0,
      min_days_non_member: v.min_days_non_member ?? 7,
      min_days_member: v.min_days_member ?? 1,
      week_1_discount_pct: v.week_1_discount_pct ?? 0,
      week_2_discount_pct: v.week_2_discount_pct ?? 0,
      week_3_discount_pct: v.week_3_discount_pct ?? 0,
      week_4_discount_pct: v.week_4_discount_pct ?? 0,
      extra_days_discount_pct: v.extra_days_discount_pct ?? 0,
      price_weekend: v.price_weekend ?? "",
      member_price_weekend: v.member_price_weekend ?? "",
      price_hourly: v.price_hourly ?? "",
      price_rtl_3_monthly: v.price_rtl_3_monthly ?? "",
      price_rtl_6_monthly: v.price_rtl_6_monthly ?? "",
      price_rtl_12_monthly: v.price_rtl_12_monthly ?? "",
      urgency_tags: (v.urgency_tags || []).join(","),
      features: (v.features || []).join(","),
    });
    setShowVehicleModal(true);
  };

  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  const uploadImageToServer = async (file) => {
    if (!file) return null;
    if (!/^image\//.test(file.type)) { toast.error("Please choose an image file (JPG, PNG, or WEBP)."); return null; }
    if (file.size > 12 * 1024 * 1024) { toast.error("Image too large (max 12 MB)."); return null; }
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API}/admin/upload/vehicle-image`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    });
    // Store the RELATIVE path (/api/uploads/...) so the URL works across
    // preview + production environments regardless of REACT_APP_BACKEND_URL.
    return { url: res.data.url, bytes: res.data.bytes, width: res.data.width, height: res.data.height };
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadImageToServer(file);
      if (!uploaded) return;
      setForm((f) => ({ ...f, image_url: uploaded.url }));
      toast.success(`Image uploaded (${Math.round(uploaded.bytes / 1024)} KB, ${uploaded.width}\u00d7${uploaded.height})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (files) => {
    if (!files || files.length === 0) return;
    setGalleryUploading(true);
    try {
      const arr = Array.from(files);
      const uploads = [];
      for (const f of arr) {
        const u = await uploadImageToServer(f);
        if (u) uploads.push(u.url);
      }
      if (uploads.length) {
        setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...uploads] }));
        toast.success(`Added ${uploads.length} image${uploads.length > 1 ? "s" : ""} to gallery`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gallery upload failed");
    } finally {
      setGalleryUploading(false);
    }
  };

  const clearMainImage = () => {
    setForm((f) => ({ ...f, image_url: "" }));
    toast.success("Main image cleared. Save to persist.");
  };

  const removeGalleryImage = (idx) => {
    setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((_, i) => i !== idx) }));
  };

  const promoteGalleryImage = (idx) => {
    setForm((f) => {
      const gallery = [...(f.gallery || [])];
      const chosen = gallery[idx];
      if (!chosen) return f;
      // Swap: current main goes into gallery (if any), chosen becomes main
      const nextGallery = gallery.filter((_, i) => i !== idx);
      if (f.image_url) nextGallery.unshift(f.image_url);
      return { ...f, image_url: chosen, gallery: nextGallery };
    });
    toast.success("Image set as main. Save to persist.");
  };

  const saveVehicle = async () => {
    try {
      const parseOpt = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
      const payload = {
        ...form,
        image_url: form.image_url || "",
        gallery: Array.isArray(form.gallery) ? form.gallery : [],
        // Core pricing
        governing_monthly_price: num(form.governing_monthly_price),
        price_weekly: num(form.price_weekly),
        price_monthly: num(form.price_monthly),
        member_price_daily: num(form.member_price_daily),
        member_price_weekly: num(form.member_price_weekly),
        member_price_monthly: num(form.member_price_monthly),
        member_pricing_mode: form.member_pricing_mode || "fixed",
        member_discount_percent: num(form.member_discount_percent),
        min_days_non_member: parseInt(form.min_days_non_member) || 7,
        min_days_member: parseInt(form.min_days_member) || 1,
        // Per-week discounts
        week_1_discount_pct: num(form.week_1_discount_pct),
        week_2_discount_pct: num(form.week_2_discount_pct),
        week_3_discount_pct: num(form.week_3_discount_pct),
        week_4_discount_pct: num(form.week_4_discount_pct),
        extra_days_discount_pct: num(form.extra_days_discount_pct),
        // Optional
        price_hourly: parseOpt(form.price_hourly),
        price_weekend: parseOpt(form.price_weekend),
        member_price_weekend: parseOpt(form.member_price_weekend),
        price_rtl_3_monthly: parseOpt(form.price_rtl_3_monthly),
        price_rtl_6_monthly: parseOpt(form.price_rtl_6_monthly),
        price_rtl_12_monthly: parseOpt(form.price_rtl_12_monthly),
        // Common
        deposit: num(form.deposit),
        seats: parseInt(form.seats) || 5,
        year: parseInt(form.year) || 2024,
        urgency_tags: (form.urgency_tags || "").split(",").map(s => s.trim()).filter(Boolean),
        features: (form.features || "").split(",").map(s => s.trim()).filter(Boolean),
        ai_enrich: !!form.ai_enrich,
      };
      // Do not send legacy price_daily field
      delete payload.price_daily;
      if (editing) {
        await axios.patch(`${API}/vehicles/${editing.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        const res = await axios.post(`${API}/vehicles`, payload, { timeout: 60000 });
        if (res.data?.ai_enriched) {
          toast.success("Vehicle added — AI enriched description & specs");
        } else if (payload.ai_enrich) {
          toast.success("Vehicle added (AI enrichment skipped — see console)");
        } else {
          toast.success("Vehicle added");
        }
      }
      setShowVehicleModal(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const reenrich = async (v) => {
    setEnrichingId(v.id);
    try {
      const res = await axios.post(`${API}/vehicles/${v.id}/enrich`, {}, { timeout: 60000 });
      const fields = (res.data?.enriched_fields || []).filter(f => f !== "ai_enriched");
      toast.success(`AI re-enriched: ${fields.join(", ") || "no fields"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI enrichment failed");
    } finally {
      setEnrichingId(null);
    }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try { await axios.delete(`${API}/vehicles/${id}`); toast.success("Vehicle deleted"); await load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const setBookingStatus = async (id, s) => {
    try {
      await axios.patch(`${API}/bookings/${id}/status`, { status: s });
      toast.success(`Booking ${s}`);
      await load();
    } catch (e) { toast.error("Update failed"); }
  };

  // Aggregations for charts (defined before any early returns to satisfy Rules of Hooks)
  const bookingsByStatus = useMemo(() => {
    const map = { pending: 0, confirmed: 0, completed: 0, rejected: 0, cancelled: 0 };
    for (const b of bookings) if (map[b.status] !== undefined) map[b.status] += 1;
    return Object.entries(map).map(([k, v]) => ({ status: STATUS_LABELS[k] || k, count: v }));
  }, [bookings]);

  const revenueTrend = useMemo(() => {
    // Naive: group by day
    const groups = {};
    for (const b of bookings) {
      if (b.status !== "confirmed" && b.status !== "completed") continue;
      const d = (b.created_at || "").slice(0, 10);
      const amount = b.is_member_pricing ? (b.member_total || 0) : (b.standard_total || 0);
      groups[d] = (groups[d] || 0) + amount;
    }
    const rows = Object.entries(groups).sort(([a],[b]) => a.localeCompare(b)).map(([d, v]) => ({ date: d, revenue: Math.round(v) }));
    return rows.length > 0 ? rows : [{ date: "today", revenue: 0 }];
  }, [bookings]);

  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/portal" replace />;

  const tabs = [
    { key: "dashboard", label: "Overview", icon: LayoutDashboard },
    { key: "vehicles", label: "Fleet", icon: Car },
    { key: "brands", label: "Brands", icon: ImageIcon },
    { key: "bookings", label: "Bookings", icon: ClipboardList },
    { key: "customers", label: "Customers", icon: Users2 },
    { key: "members", label: "Members", icon: Sparkles },
    { key: "business", label: "Business apps", icon: Briefcase },
  ];

  return (
    <PortalLayout tabs={tabs} activeTab={activeTab} onTabChange={setTab} kind="admin">
      {activeTab === "dashboard" && (
        <div data-testid="admin-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Car, k: "Vehicles", v: analytics?.total_vehicles ?? 0, sub: `${analytics?.available_vehicles ?? 0} available` },
              { icon: ClipboardList, k: "Bookings", v: analytics?.total_bookings ?? 0, sub: `${analytics?.pending_bookings ?? 0} pending` },
              { icon: Users2, k: "Customers", v: analytics?.total_customers ?? 0, sub: `${analytics?.total_members ?? 0} members` },
              { icon: Wallet, k: "Revenue est.", v: currency(analytics?.total_revenue ?? 0), sub: `Bookings + membership` },
            ].map((s, i) => (
              <div key={i} className="card-clean p-5">
                <s.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground">{s.k}</div>
                <div className="font-display text-2xl font-semibold mt-1" data-testid={`admin-stat-${s.k.toLowerCase().replace(/\W+/g,'-')}`}>{s.v}</div>
                <div className="text-xs text-muted-foreground">{s.sub}</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mt-4">
            <div className="card-clean p-5 lg:col-span-2">
              <div className="font-display text-lg font-semibold">Bookings by status</div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={bookingsByStatus}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="status" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[8,8,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="card-clean p-5">
              <div className="font-display text-lg font-semibold">Revenue trend</div>
              <div className="h-64">
                <ResponsiveContainer>
                  <LineChart data={revenueTrend}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="date" fontSize={10} />
                    <YAxis fontSize={10} />
                    <Tooltip />
                    <Line dataKey="revenue" stroke="hsl(var(--charcoal-950))" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "vehicles" && (
        <div>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-2xl font-semibold">Fleet</h2>
              <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1"><Wand2 className="h-3 w-3 text-[hsl(var(--primary))]" /> New vehicles are automatically enriched by AI on upload.</p>
            </div>
            <Button onClick={openNew} className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="admin-add-vehicle"><Plus className="h-4 w-4 mr-1" /> Add vehicle</Button>
          </div>
          <div className="card-clean mt-4 overflow-x-auto" data-testid="admin-vehicles-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Monthly</TableHead>
                  <TableHead>Weekly</TableHead>
                  <TableHead>Deposit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicles.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img src={v.image_url} alt={v.name} className="h-10 w-14 rounded-md object-cover bg-[hsl(var(--charcoal-100))]" />
                        <div>
                          <div className="text-sm font-medium flex items-center gap-1.5">
                            {v.name}
                            {v.ai_enriched && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full badge-blue" title="Enriched by AI">
                                <Wand2 className="h-2.5 w-2.5" /> AI
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{v.brand} · {v.year}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{CATEGORY_LABELS[v.category]}</TableCell>
                    <TableCell className="text-sm">{currency(v.price_monthly)}</TableCell>
                    <TableCell className="text-sm">{currency(v.price_weekly)}</TableCell>
                    <TableCell className="text-sm">{currency(v.deposit)}</TableCell>
                    <TableCell><span className={`text-[11px] px-2 py-1 rounded-full ${v.available ? "badge-availability" : "badge-danger"}`}>{v.available ? "Available" : "Hidden"}</span></TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => reenrich(v)} disabled={enrichingId === v.id} title="Re-enrich with AI" data-testid={`enrich-${v.id}`}>
                        <Wand2 className={`h-3.5 w-3.5 ${enrichingId === v.id ? "animate-pulse text-[hsl(var(--primary))]" : ""}`} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`edit-${v.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteVehicle(v.id)} data-testid={`delete-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Dialog open={showVehicleModal} onOpenChange={setShowVehicleModal}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit vehicle" : "Add vehicle"}</DialogTitle></DialogHeader>

              {!editing && (
                <div className="rounded-xl border border-[hsl(var(--blue-400))] bg-[hsl(var(--blue-100))]/60 p-4 flex items-start gap-3" data-testid="ai-enrich-banner">
                  <Wand2 className="h-5 w-5 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground">AI enrichment on upload</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Fill in <span className="font-medium text-foreground">Brand</span>, <span className="font-medium text-foreground">Name</span>, <span className="font-medium text-foreground">Year</span>, and <span className="font-medium text-foreground">Category</span> — our AI agent will auto-generate the description, features, seats, transmission, drivetrain, fuel type, and urgency tags. Leave those fields blank to let AI fill them, or provide your own to override.
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Switch checked={!!form.ai_enrich} onCheckedChange={(v) => setForm({ ...form, ai_enrich: v })} data-testid="vform-ai-enrich" />
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">AI on</span>
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vform-name" /></div>
                <div><Label>Brand</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="vform-brand" /></div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORY_ORDER.map((k) => <SelectItem key={k} value={k}>{CATEGORY_LABELS[k]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Year</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>

                {/* Image upload + auto-format */}
                <div className="sm:col-span-2" data-testid="vform-image-uploader">
                  <Label>Vehicle image</Label>
                  <div className="mt-1 rounded-xl border border-dashed border-border p-4 bg-secondary/30">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="relative h-24 w-40 rounded-lg overflow-hidden bg-[hsl(var(--charcoal-100))] flex-shrink-0 flex items-center justify-center group">
                        {form.image_url ? (
                          <>
                            <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" data-testid="vform-image-preview" onError={(e) => { e.currentTarget.style.opacity = "0.35"; }} />
                            <button
                              type="button"
                              onClick={clearMainImage}
                              aria-label="Remove main image"
                              title="Remove main image"
                              className="absolute top-1 right-1 inline-flex items-center justify-center h-6 w-6 rounded-full bg-black/70 text-white hover:bg-red-600 transition-colors shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                              data-testid="vform-image-clear"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : (
                          <ImageIcon className="h-6 w-6 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 w-full">
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-sm cursor-pointer hover:bg-secondary transition-colors" data-testid="vform-image-upload-label">
                            <Upload className="h-4 w-4 text-[hsl(var(--primary))]" />
                            {uploading ? "Uploading…" : "Upload image"}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/heic"
                              className="hidden"
                              disabled={uploading}
                              onChange={(e) => handleImageUpload(e.target.files?.[0])}
                              data-testid="vform-image-upload-input"
                            />
                          </label>
                          {form.image_url && (
                            <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl" onClick={clearMainImage} data-testid="vform-image-clear-btn">
                              <X className="h-4 w-4 mr-1" /> Clear
                            </Button>
                          )}
                        </div>
                        <div className="mt-2 text-[11px] text-muted-foreground">Auto-formats to 1600×1000 (16:10), JPEG q85. Max 12 MB. Or paste an external URL below.</div>
                        <div className="mt-2 flex items-center gap-2">
                          <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://… or /api/uploads/vehicles/…" data-testid="vform-image" />
                          {form.image_url && (
                            <Button type="button" variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-red-600 hover:bg-red-50" onClick={clearMainImage} aria-label="Clear image URL" data-testid="vform-image-url-clear">
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gallery editor */}
                <div className="sm:col-span-2" data-testid="vform-gallery-editor">
                  <div className="flex items-center justify-between">
                    <Label>Gallery ({(form.gallery || []).length})</Label>
                    <label className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-1.5 text-xs cursor-pointer hover:bg-secondary transition-colors" data-testid="vform-gallery-upload-label">
                      <ImagePlus className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                      {galleryUploading ? "Uploading…" : "Add images"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic"
                        multiple
                        className="hidden"
                        disabled={galleryUploading}
                        onChange={(e) => { handleGalleryUpload(e.target.files); e.target.value = ""; }}
                        data-testid="vform-gallery-upload-input"
                      />
                    </label>
                  </div>
                  <div className="mt-2 rounded-xl border border-dashed border-border p-3 bg-secondary/30 min-h-[80px]">
                    {(form.gallery || []).length === 0 ? (
                      <p className="text-[11px] text-muted-foreground text-center py-4">No additional images yet. Upload to add gallery shots. Click ✕ to remove any image.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2">
                        {(form.gallery || []).map((src, idx) => (
                          <div key={`${src}-${idx}`} className="relative group aspect-[4/3] rounded-lg overflow-hidden bg-[hsl(var(--charcoal-100))] border border-border" data-testid={`vform-gallery-item-${idx}`}>
                            <img src={src} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.35"; }} />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-start justify-end p-1 gap-1">
                              <button
                                type="button"
                                onClick={() => promoteGalleryImage(idx)}
                                aria-label={`Set gallery image ${idx + 1} as main`}
                                title="Set as main image"
                                className="opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center justify-center h-6 px-1.5 rounded-md bg-white/95 text-[10px] font-medium text-[hsl(var(--charcoal-900))] hover:bg-white transition-all shadow"
                                data-testid={`vform-gallery-promote-${idx}`}
                              >
                                Set main
                              </button>
                              <button
                                type="button"
                                onClick={() => removeGalleryImage(idx)}
                                aria-label={`Remove gallery image ${idx + 1}`}
                                title="Remove image"
                                className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-black/70 text-white hover:bg-red-600 transition-colors shadow"
                                data-testid={`vform-gallery-remove-${idx}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

                {/* Pricing block — NEW ENGINE */}
                <div className="sm:col-span-2 rounded-xl border border-border p-4 bg-white space-y-5" data-testid="vform-pricing-block">
                  {/* Core rates */}
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Core pricing (CAD)</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label>Governing monthly price</Label>
                        <Input type="number" value={form.governing_monthly_price} onChange={(e) => setForm({ ...form, governing_monthly_price: e.target.value })} data-testid="vform-governing-monthly" />
                        <p className="mt-1 text-[10px] text-muted-foreground">Drives standard daily rate = Gov ÷ days in that month.</p>
                      </div>
                      <div>
                        <Label>Standard daily (auto)</Label>
                        <div className="mt-1 h-10 flex items-center px-3 rounded-md border border-border bg-secondary/40 text-sm tabular-nums" data-testid="vform-standard-daily-preview">
                          {form.governing_monthly_price > 0 ? (
                            (() => {
                              const g = Number(form.governing_monthly_price);
                              const jul = (g / 31).toFixed(2);
                              const jun = (g / 30).toFixed(2);
                              const feb = (g / 28).toFixed(2);
                              return `$${jul}–$${feb}/day`;
                            })()
                          ) : (
                            <span className="text-muted-foreground">Set governing monthly to preview</span>
                          )}
                        </div>
                        <p className="mt-1 text-[10px] text-muted-foreground">Range shown across 28–31-day calendar months.</p>
                      </div>
                      <div>
                        <Label>Standard weekly</Label>
                        <Input type="number" value={form.price_weekly} onChange={(e) => setForm({ ...form, price_weekly: e.target.value })} data-testid="vform-price-weekly" />
                      </div>
                      <div>
                        <Label>Standard monthly rental price</Label>
                        <Input type="number" value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} data-testid="vform-price-monthly" />
                        <p className="mt-1 text-[10px] text-muted-foreground">Charged only at 29 or 30 days (or first 30 for 30+ rentals).</p>
                      </div>
                      <div>
                        <Label>Refundable deposit</Label>
                        <Input type="number" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} data-testid="vform-deposit" />
                      </div>
                      <div>
                        <Label>Hourly rate (member only)</Label>
                        <Input type="number" value={form.price_hourly} onChange={(e) => setForm({ ...form, price_hourly: e.target.value })} placeholder="e.g. 40" data-testid="vform-price-hourly" />
                        <p className="mt-1 text-[10px] text-muted-foreground">Only Drive Alfa Club members see hourly rentals.</p>
                      </div>
                    </div>
                  </div>

                  {/* Member pricing */}
                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Member pricing</div>
                      <div className="inline-flex rounded-lg border border-border p-0.5 text-xs">
                        <button
                          type="button"
                          className={`px-3 py-1 rounded-md transition-colors ${form.member_pricing_mode === "fixed" ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground"}`}
                          onClick={() => setForm({ ...form, member_pricing_mode: "fixed" })}
                          data-testid="vform-member-mode-fixed"
                        >Fixed prices</button>
                        <button
                          type="button"
                          className={`px-3 py-1 rounded-md transition-colors ${form.member_pricing_mode === "percent_off_standard" ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground"}`}
                          onClick={() => setForm({ ...form, member_pricing_mode: "percent_off_standard" })}
                          data-testid="vform-member-mode-percent"
                        >% off standard</button>
                      </div>
                    </div>
                    {form.member_pricing_mode === "percent_off_standard" ? (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <Label>Member % off standard</Label>
                          <Input type="number" value={form.member_discount_percent} onChange={(e) => setForm({ ...form, member_discount_percent: e.target.value })} placeholder="e.g. 15" data-testid="vform-member-pct" />
                          <p className="mt-1 text-[10px] text-muted-foreground">Applied to weekly + monthly + daily.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <Label>Member daily</Label>
                          <Input type="number" value={form.member_price_daily} onChange={(e) => setForm({ ...form, member_price_daily: e.target.value })} data-testid="vform-mprice-daily" />
                        </div>
                        <div>
                          <Label>Member weekly</Label>
                          <Input type="number" value={form.member_price_weekly} onChange={(e) => setForm({ ...form, member_price_weekly: e.target.value })} data-testid="vform-mprice-weekly" />
                        </div>
                        <div>
                          <Label>Member monthly rental</Label>
                          <Input type="number" value={form.member_price_monthly} onChange={(e) => setForm({ ...form, member_price_monthly: e.target.value })} data-testid="vform-mprice-monthly" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Rental minimums */}
                  <div className="pt-4 border-t border-border">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Rental minimums</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label>Minimum days (non-members)</Label>
                        <Input type="number" value={form.min_days_non_member} onChange={(e) => setForm({ ...form, min_days_non_member: e.target.value })} data-testid="vform-min-non" />
                      </div>
                      <div>
                        <Label>Minimum days (Drive Alfa Club members)</Label>
                        <Input type="number" value={form.min_days_member} onChange={(e) => setForm({ ...form, min_days_member: e.target.value })} data-testid="vform-min-mem" />
                      </div>
                    </div>
                  </div>

                  {/* Per-week discount tiers */}
                  <div className="pt-4 border-t border-border">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Per-week discount tiers (%)</div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <div><Label>Week 1</Label><Input type="number" value={form.week_1_discount_pct} onChange={(e) => setForm({ ...form, week_1_discount_pct: e.target.value })} data-testid="vform-week-1" /></div>
                      <div><Label>Week 2</Label><Input type="number" value={form.week_2_discount_pct} onChange={(e) => setForm({ ...form, week_2_discount_pct: e.target.value })} data-testid="vform-week-2" /></div>
                      <div><Label>Week 3</Label><Input type="number" value={form.week_3_discount_pct} onChange={(e) => setForm({ ...form, week_3_discount_pct: e.target.value })} data-testid="vform-week-3" /></div>
                      <div><Label>Week 4</Label><Input type="number" value={form.week_4_discount_pct} onChange={(e) => setForm({ ...form, week_4_discount_pct: e.target.value })} data-testid="vform-week-4" /></div>
                      <div><Label>Extra days</Label><Input type="number" value={form.extra_days_discount_pct} onChange={(e) => setForm({ ...form, extra_days_discount_pct: e.target.value })} data-testid="vform-extra-days" /></div>
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">Discounts apply only to their assigned week. Extra-days % applies to any leftover days past whole weeks. Every rental day is always charged.</p>
                  </div>

                  {/* Optional legacy */}
                  <div className="pt-4 border-t border-border">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Optional — Weekend + long-term Rental Term Leasing</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div><Label>Weekend flat (std)</Label><Input type="number" value={form.price_weekend} onChange={(e) => setForm({ ...form, price_weekend: e.target.value })} placeholder="Optional" data-testid="vform-price-weekend" /></div>
                      <div><Label>Weekend flat (member)</Label><Input type="number" value={form.member_price_weekend} onChange={(e) => setForm({ ...form, member_price_weekend: e.target.value })} placeholder="Optional" data-testid="vform-mprice-weekend" /></div>
                      <div />
                      <div><Label>3-month RTL / mo</Label><Input type="number" value={form.price_rtl_3_monthly} onChange={(e) => setForm({ ...form, price_rtl_3_monthly: e.target.value })} placeholder="Optional" data-testid="vform-rtl-3" /></div>
                      <div><Label>6-month RTL / mo</Label><Input type="number" value={form.price_rtl_6_monthly} onChange={(e) => setForm({ ...form, price_rtl_6_monthly: e.target.value })} placeholder="Optional" data-testid="vform-rtl-6" /></div>
                      <div><Label>12-month RTL / mo</Label><Input type="number" value={form.price_rtl_12_monthly} onChange={(e) => setForm({ ...form, price_rtl_12_monthly: e.target.value })} placeholder="Optional" data-testid="vform-rtl-12" /></div>
                    </div>
                  </div>
                </div>

                <div><Label>Seats</Label><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} /></div>
                <div><Label>Transmission</Label><Input value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })} /></div>
                <div><Label>Drivetrain</Label><Input value={form.drivetrain} onChange={(e) => setForm({ ...form, drivetrain: e.target.value })} /></div>
                <div><Label>Fuel</Label><Input value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })} /></div>
                <div><Label>Mileage</Label><Input value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Urgency tags (comma-separated)</Label><Input value={form.urgency_tags} onChange={(e) => setForm({ ...form, urgency_tags: e.target.value })} placeholder="High demand, Member pricing available" /></div>
                <div className="sm:col-span-2"><Label>Features (comma-separated)</Label><Input value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder="Heated seats, Apple CarPlay" /></div>
                <div className="flex items-center gap-3"><Switch checked={!!form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="vform-available" /> <span className="text-sm">Available for booking</span></div>
                <div className="flex items-center gap-3"><Switch checked={!!form.rideshare_ready} onCheckedChange={(v) => setForm({ ...form, rideshare_ready: v })} /> <span className="text-sm">Rideshare-ready</span></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowVehicleModal(false)}>Cancel</Button>
                <Button className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" onClick={saveVehicle} data-testid="vform-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Pricing Calculator */}
          <PricingCalculator vehicles={vehicles} />
        </div>
      )}

      {activeTab === "bookings" && (
        <div>
          <h2 className="font-display text-2xl font-semibold">Bookings</h2>
          <div className="card-clean mt-4 overflow-x-auto" data-testid="admin-bookings-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((b) => {
                  const s = statusBadge(b.status);
                  return (
                    <TableRow key={b.id}>
                      <TableCell className="font-mono text-xs">{b.booking_ref}</TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{b.user_name}</div>
                        <div className="text-xs text-muted-foreground">{b.user_email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <img src={b.vehicle_image} alt={b.vehicle_name} className="h-8 w-12 rounded object-cover" />
                          <div className="text-sm">{b.vehicle_name}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{formatDate(b.start_date)}<br />{formatDate(b.end_date)}</TableCell>
                      <TableCell className="text-xs">{PLAN_LABELS[b.plan_type]}</TableCell>
                      <TableCell className="text-sm">{currency(b.is_member_pricing ? b.member_total : b.standard_total)}</TableCell>
                      <TableCell><span className={`inline-flex items-center gap-1 rounded-full text-[11px] px-2 py-1 ${s.cls}`}><s.icon className="h-3 w-3" /> {STATUS_LABELS[b.status]}</span></TableCell>
                      <TableCell className="whitespace-nowrap">
                        {b.status === "pending" && (
                          <>
                            <Button size="sm" className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))] h-8 mr-1" onClick={() => setBookingStatus(b.id, "confirmed")} data-testid={`approve-${b.id}`}>Approve</Button>
                            <Button size="sm" variant="outline" className="h-8" onClick={() => setBookingStatus(b.id, "rejected")} data-testid={`reject-${b.id}`}>Reject</Button>
                          </>
                        )}
                        {b.status === "confirmed" && (
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setBookingStatus(b.id, "completed")}>Mark completed</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {activeTab === "customers" && (
        <div>
          <h2 className="font-display text-2xl font-semibold">Customers</h2>
          <div className="card-clean mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Docs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.full_name}</TableCell>
                    <TableCell className="text-sm">{c.email}</TableCell>
                    <TableCell className="text-sm">{c.phone}</TableCell>
                    <TableCell className="text-sm">{c.role}</TableCell>
                    <TableCell>{c.is_member ? <span className="badge-blue rounded-full px-2 py-1 text-[11px]">Member</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-sm">{(c.documents || []).length}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {activeTab === "members" && (
        <div>
          <h2 className="font-display text-2xl font-semibold">Drive Alfa Club members</h2>
          <div className="card-clean mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {memberships.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="font-medium">{m.user_name}</TableCell>
                    <TableCell className="text-sm">{m.user_email}</TableCell>
                    <TableCell className="text-sm">{formatDate(m.start_date)}</TableCell>
                    <TableCell className="text-sm">{formatDate(m.end_date)}</TableCell>
                    <TableCell className="text-sm">{currency(m.price_paid)}</TableCell>
                    <TableCell><span className="badge-blue rounded-full px-2 py-1 text-[11px]">{m.status}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {activeTab === "business" && (
        <div>
          <h2 className="font-display text-2xl font-semibold">Business (Alfa One Path) applications</h2>
          <div className="card-clean mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Need</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businessApps.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <div className="font-medium text-sm">{a.user_name}</div>
                      <div className="text-xs text-muted-foreground">{a.user_email}</div>
                    </TableCell>
                    <TableCell className="text-sm">{a.business_name}</TableCell>
                    <TableCell className="text-sm">{a.business_type}</TableCell>
                    <TableCell className="text-sm">{a.vehicle_need}</TableCell>
                    <TableCell className="text-sm">{a.estimated_duration}</TableCell>
                    <TableCell><span className="badge-blue rounded-full px-2 py-1 text-[11px]">{a.status}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {activeTab === "brands" && (
        <BrandsPanel />
      )}
    </PortalLayout>
  );
}


// ============================================================================
// Pricing Calculator (embedded on the Fleet tab)
// Uses the same lib/pricing engine as the customer-facing Booking Planner so
// admin quotes stay in sync with what customers see.
// ============================================================================

function pad(n) { return String(n).padStart(2, "0"); }
function toLocalISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function addHours(iso, h) {
  const d = new Date(iso); d.setHours(d.getHours() + h);
  return toLocalISO(d);
}

const CALC_PRESETS = [
  { key: "h4", label: "4 hours", hours: 4 },
  { key: "d1", label: "1 day", hours: 24 },
  { key: "d3", label: "3 days", hours: 72 },
  { key: "d9", label: "9 days", hours: 24 * 9 },
  { key: "w1", label: "1 week", hours: 24 * 7 },
  { key: "w2", label: "2 weeks", hours: 24 * 14 },
  { key: "m1", label: "1 month", hours: 24 * 30 },
  { key: "m3", label: "3 months", hours: 24 * 90 },
];

function PricingCalculator({ vehicles }) {
  const [mode, setMode] = useState("vehicle"); // "vehicle" | "custom"
  const [selectedId, setSelectedId] = useState(vehicles[0]?.id || "");
  const [custom, setCustom] = useState({
    governing_monthly_price: 5187,
    price_weekly: 1130,
    price_monthly: 3990,
    member_price_daily: 147,
    member_price_weekly: 960.5,
    member_price_monthly: 3391.5,
    member_pricing_mode: "fixed",
    member_discount_percent: 15,
    week_1_discount_pct: 0,
    week_2_discount_pct: 15,
    week_3_discount_pct: 17,
    week_4_discount_pct: 0,
    extra_days_discount_pct: 0,
    min_days_non_member: 7,
    min_days_member: 1,
    deposit: 750,
  });

  useEffect(() => {
    if (!selectedId && vehicles[0]?.id) setSelectedId(vehicles[0].id);
  }, [vehicles, selectedId]);

  const nowIso = useMemo(() => {
    const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1);
    return toLocalISO(d);
  }, []);
  const [start, setStart] = useState(nowIso);
  const [end, setEnd] = useState(addHours(nowIso, 24 * 9)); // default to the "9 days" example

  const applyPreset = (p) => { setStart(nowIso); setEnd(addHours(nowIso, p.hours)); };

  const vehicle = useMemo(() => {
    if (mode === "vehicle") return vehicles.find((v) => v.id === selectedId) || null;
    // Build a synthetic vehicle from custom inputs (new engine fields)
    return {
      id: "calc-custom",
      governing_monthly_price: Number(custom.governing_monthly_price) || 0,
      price_weekly: Number(custom.price_weekly) || 0,
      price_monthly: Number(custom.price_monthly) || 0,
      member_price_daily: Number(custom.member_price_daily) || 0,
      member_price_weekly: Number(custom.member_price_weekly) || 0,
      member_price_monthly: Number(custom.member_price_monthly) || 0,
      member_pricing_mode: custom.member_pricing_mode || "fixed",
      member_discount_percent: Number(custom.member_discount_percent) || 0,
      week_1_discount_pct: Number(custom.week_1_discount_pct) || 0,
      week_2_discount_pct: Number(custom.week_2_discount_pct) || 0,
      week_3_discount_pct: Number(custom.week_3_discount_pct) || 0,
      week_4_discount_pct: Number(custom.week_4_discount_pct) || 0,
      extra_days_discount_pct: Number(custom.extra_days_discount_pct) || 0,
      min_days_non_member: Number(custom.min_days_non_member) || 7,
      min_days_member: Number(custom.min_days_member) || 1,
      deposit: Number(custom.deposit) || 0,
    };
  }, [mode, selectedId, vehicles, custom]);

  const std = useMemo(() => (vehicle ? computePricing({ vehicle, startISO: start, endISO: end, isMember: false }) : null), [vehicle, start, end]);
  const mem = useMemo(() => (vehicle ? computePricing({ vehicle, startISO: start, endISO: end, isMember: true }) : null), [vehicle, start, end]);

  return (
    <div className="mt-10 card-clean p-5 sm:p-6" data-testid="pricing-calculator">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-[hsl(var(--blue-100))] flex items-center justify-center">
          <Calculator className="h-5 w-5 text-[hsl(var(--primary))]" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Admin tool</div>
          <h3 className="font-display text-xl sm:text-2xl font-semibold">Pricing calculator</h3>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className={`chip ${mode === "vehicle" ? "chip-active" : ""}`}
            onClick={() => setMode("vehicle")}
            data-testid="calc-mode-vehicle"
          >Use a vehicle</button>
          <button
            type="button"
            className={`chip ${mode === "custom" ? "chip-active" : ""}`}
            onClick={() => setMode("custom")}
            data-testid="calc-mode-custom"
          >Custom rates</button>
        </div>
      </div>

      <div className="mt-5 grid lg:grid-cols-12 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-5 space-y-4">
          {mode === "vehicle" ? (
            <div>
              <Label>Vehicle</Label>
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="calc-vehicle-select"><SelectValue placeholder="Choose a vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.brand} {v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {vehicle && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                  <div className="rounded-lg border border-border p-2 bg-secondary/40"><div>Gov/mo</div><div className="text-foreground font-medium tabular-nums">{currency(vehicle.governing_monthly_price)}</div></div>
                  <div className="rounded-lg border border-border p-2 bg-secondary/40"><div>Std/wk</div><div className="text-foreground font-medium tabular-nums">{currency(vehicle.price_weekly)}</div></div>
                  <div className="rounded-lg border border-border p-2 bg-secondary/40"><div>Monthly deal</div><div className="text-foreground font-medium tabular-nums">{currency(vehicle.price_monthly)}</div></div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Governing monthly</Label><Input type="number" value={custom.governing_monthly_price} onChange={(e) => setCustom({ ...custom, governing_monthly_price: e.target.value })} data-testid="calc-governing" /></div>
              <div><Label>Std weekly</Label><Input type="number" value={custom.price_weekly} onChange={(e) => setCustom({ ...custom, price_weekly: e.target.value })} /></div>
              <div><Label>Std monthly deal</Label><Input type="number" value={custom.price_monthly} onChange={(e) => setCustom({ ...custom, price_monthly: e.target.value })} /></div>
              <div><Label>Mbr daily</Label><Input type="number" value={custom.member_price_daily} onChange={(e) => setCustom({ ...custom, member_price_daily: e.target.value })} data-testid="calc-mbr-daily" /></div>
              <div><Label>Mbr weekly</Label><Input type="number" value={custom.member_price_weekly} onChange={(e) => setCustom({ ...custom, member_price_weekly: e.target.value })} /></div>
              <div><Label>Mbr monthly</Label><Input type="number" value={custom.member_price_monthly} onChange={(e) => setCustom({ ...custom, member_price_monthly: e.target.value })} /></div>
              <div><Label>W1 %</Label><Input type="number" value={custom.week_1_discount_pct} onChange={(e) => setCustom({ ...custom, week_1_discount_pct: e.target.value })} /></div>
              <div><Label>W2 %</Label><Input type="number" value={custom.week_2_discount_pct} onChange={(e) => setCustom({ ...custom, week_2_discount_pct: e.target.value })} /></div>
              <div><Label>W3 %</Label><Input type="number" value={custom.week_3_discount_pct} onChange={(e) => setCustom({ ...custom, week_3_discount_pct: e.target.value })} /></div>
              <div><Label>W4 %</Label><Input type="number" value={custom.week_4_discount_pct} onChange={(e) => setCustom({ ...custom, week_4_discount_pct: e.target.value })} /></div>
              <div><Label>Extra days %</Label><Input type="number" value={custom.extra_days_discount_pct} onChange={(e) => setCustom({ ...custom, extra_days_discount_pct: e.target.value })} /></div>
              <div><Label>Deposit</Label><Input type="number" value={custom.deposit} onChange={(e) => setCustom({ ...custom, deposit: e.target.value })} /></div>
            </div>
          )}

          <div>
            <Label>Presets</Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="calc-presets">
              {CALC_PRESETS.map((p) => (
                <button key={p.key} type="button" className="chip" onClick={() => applyPreset(p)} data-testid={`calc-preset-${p.key}`}>{p.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Pick up</Label>
              <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="calc-start" />
            </div>
            <div>
              <Label>Return</Label>
              <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} min={start} data-testid="calc-end" />
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-border p-5 bg-white" data-testid="calc-result">
            {vehicle ? (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <PriceColumn title="Standard" pricing={std} accent={false} />
                  <PriceColumn title="Drive Alfa Club member" pricing={mem} accent={true} />
                </div>
                {std?.ok && mem?.ok && (
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Member savings on this scenario</span>
                    <span className="font-medium text-[hsl(var(--primary))] tabular-nums" data-testid="calc-savings">
                      {currency(Math.max(0, (std.totalUsed || 0) - (mem.totalUsed || 0)))}
                    </span>
                  </div>
                )}
                {vehicle.deposit != null && (
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Refundable deposit</span>
                    <span className="tabular-nums">{currency(vehicle.deposit)}</span>
                  </div>
                )}
                <div className="mt-4 text-[11px] text-muted-foreground inline-flex items-center gap-1">
                  <Sparkle className="h-3 w-3" /> This calculator uses the same engine as the customer Booking Planner.
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Pick a vehicle or enter custom rates to compute a quote.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PriceColumn({ title, pricing, accent }) {
  const isOk = pricing?.ok;
  const total = pricing?.totalUsed || 0;
  const breakdown = (pricing?.used === "member" ? pricing?.member?.breakdown : pricing?.standard?.breakdown) || [];
  const unitLabel = (u, n) => (u === "hour" ? (n > 1 ? "hours" : "hour") : u === "day" ? (n > 1 ? "days" : "day") : u === "week" ? (n > 1 ? "weeks" : "week") : (n > 1 ? "months" : "month"));
  return (
    <div className={`rounded-xl border p-4 ${accent ? "border-[hsl(var(--blue-400))] bg-[hsl(var(--blue-100))]/40" : "border-border bg-secondary/40"}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{title}</div>
        {accent && <span className="inline-flex items-center gap-1 text-[10px] text-[hsl(var(--primary))]"><Sparkles className="h-3 w-3" /> Member</span>}
      </div>
      {!isOk ? (
        <div className="mt-3 text-xs text-[hsl(var(--danger))]" data-testid={`calc-col-${accent ? "member" : "standard"}-error`}>
          {pricing?.error || "Set a duration to see pricing."}
        </div>
      ) : (
        <>
          <div className="mt-2 space-y-1.5">
            {breakdown.map((b, i) => (
              <div key={i} className="flex items-center justify-between text-xs gap-3">
                <span className="text-muted-foreground min-w-0 flex-1">
                  {b.note ? (
                    <span className="tabular-nums font-medium text-foreground">{b.note}</span>
                  ) : (
                    <><span className="tabular-nums font-medium text-foreground">{b.count}</span> {unitLabel(b.unit, b.count)}</>
                  )}
                  <span> · </span>
                  <span className="tabular-nums">{currency(b.rate)}</span>
                  {b.discount_pct > 0 && (
                    <span className="ml-1.5 inline-flex items-center rounded-full bg-[hsl(var(--blue-100))] text-[hsl(var(--primary))] px-1.5 py-0.5 text-[10px] font-medium">−{b.discount_pct}%</span>
                  )}
                </span>
                <span className="tabular-nums text-foreground whitespace-nowrap">{currency(b.subtotal)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-border flex items-baseline justify-between">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Total</div>
            <div className="font-display text-2xl font-semibold tabular-nums" data-testid={`calc-col-${accent ? "member" : "standard"}-total`}>{currency(total)}</div>
          </div>
        </>
      )}
    </div>
  );
}


// ============================================================================
// Brands panel — manage the "Vehicles by trusted brands" homepage carousel.
// Admin can add / edit / delete / reorder logos (transparent PNG / SVG / JPG).
// ============================================================================

function BrandsPanel() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", logo_url: "", website_url: "", display_order: 100, active: true });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Admin needs to see inactive too so pass active_only=false.
      const res = await axios.get(`${API}/brands?active_only=false`);
      const sorted = [...(res.data || [])].sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
      setBrands(sorted);
    } catch (e) {
      toast.error("Could not load brand carousel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", logo_url: "", website_url: "", display_order: (brands.length > 0 ? Math.max(...brands.map((b) => b.display_order ?? 0)) + 1 : 0), active: true });
    setShowModal(true);
  };

  const openEditBrand = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "",
      logo_url: b.logo_url || "",
      website_url: b.website_url || "",
      display_order: b.display_order ?? 100,
      active: b.active !== false,
    });
    setShowModal(true);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type) && !file.name.toLowerCase().endsWith(".svg")) {
      toast.error("Please upload a PNG, SVG, WebP, or JPG."); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload/brand-logo`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      // Keep the URL relative so it resolves against whatever backend host
      // is serving the page (preview or production).
      setForm((f) => ({ ...f, logo_url: res.data.url }));
      toast.success("Logo uploaded — normalized to 120px height.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Brand name is required."); return; }
    if (!form.logo_url?.trim()) { toast.error("Logo URL is required — upload a file or paste a URL."); return; }
    try {
      const payload = {
        name: form.name.trim(),
        logo_url: form.logo_url.trim(),
        website_url: form.website_url?.trim() || null,
        display_order: parseInt(form.display_order) || 100,
        active: !!form.active,
      };
      if (editing) {
        await axios.patch(`${API}/admin/brands/${editing.id}`, payload);
        toast.success("Brand updated");
      } else {
        await axios.post(`${API}/admin/brands`, payload);
        toast.success("Brand added");
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const removeBrand = async (b) => {
    if (!window.confirm(`Delete "${b.name}" from the homepage carousel?`)) return;
    try {
      await axios.delete(`${API}/admin/brands/${b.id}`);
      toast.success("Brand removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const moveBrand = async (index, direction) => {
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= brands.length) return;
    const next = [...brands];
    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    setBrands(next); // optimistic
    try {
      await axios.post(`${API}/admin/brands/reorder`, next.map((b) => b.id));
    } catch (e) {
      toast.error("Reorder failed — reloading");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-brands-panel">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold" data-testid="admin-brands-heading">Homepage brand carousel</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Manage the "Vehicles by trusted brands" logo strip on the homepage. Upload transparent PNGs or SVGs; sizes are normalized automatically (max height 120px). Reorder with the arrow buttons — the topmost row shows first.</p>
        </div>
        <Button onClick={openCreate} className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="admin-brands-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Add brand
        </Button>
      </div>

      {/* Live preview strip */}
      <div className="rounded-2xl border border-border bg-white">
        <div className="px-4 py-3 flex items-center justify-between border-b border-border">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Live preview</div>
          <div className="text-[11px] text-muted-foreground">{brands.filter((b) => b.active !== false).length} active</div>
        </div>
        <div className="p-4 overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-6 min-w-max" data-testid="admin-brands-preview-strip">
            {brands.filter((b) => b.active !== false).map((b) => (
              <div key={b.id} className="opacity-80 hover:opacity-100 transition-opacity h-14 flex items-center">
                <img
                  src={b.logo_url}
                  alt={b.name}
                  className="max-h-10 max-w-[140px] object-contain"
                  onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("span"), { className: "text-sm font-display font-semibold text-muted-foreground", textContent: b.name })); }}
                />
              </div>
            ))}
            {brands.filter((b) => b.active !== false).length === 0 && (
              <div className="text-sm text-muted-foreground py-4">No active brands yet. Click "Add brand" to upload your first logo.</div>
            )}
          </div>
        </div>
      </div>

      {/* Brand list */}
      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Order</TableHead>
              <TableHead>Logo</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && brands.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No brands yet. Add your first logo above.</TableCell></TableRow>
            )}
            {!loading && brands.map((b, i) => (
              <TableRow key={b.id} data-testid={`admin-brand-row-${b.id}`}>
                <TableCell className="align-middle">
                  <div className="inline-flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveBrand(i, -1)}
                      disabled={i === 0}
                      className="h-6 w-6 rounded-md border border-border text-xs disabled:opacity-30 hover:bg-secondary transition-colors"
                      aria-label={`Move ${b.name} up`}
                      data-testid={`admin-brand-up-${b.id}`}
                    >↑</button>
                    <button
                      type="button"
                      onClick={() => moveBrand(i, 1)}
                      disabled={i === brands.length - 1}
                      className="h-6 w-6 rounded-md border border-border text-xs disabled:opacity-30 hover:bg-secondary transition-colors"
                      aria-label={`Move ${b.name} down`}
                      data-testid={`admin-brand-down-${b.id}`}
                    >↓</button>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="h-12 w-24 rounded-md bg-secondary/40 border border-border flex items-center justify-center overflow-hidden">
                    <img src={b.logo_url} alt={b.name} className="max-h-10 max-w-[88px] object-contain" onError={(e) => { e.currentTarget.style.opacity = "0.35"; }} />
                  </div>
                </TableCell>
                <TableCell className="font-medium">{b.name}</TableCell>
                <TableCell>
                  {b.active !== false ? (
                    <span className="badge-blue rounded-full px-2 py-1 text-[11px]">Active</span>
                  ) : (
                    <span className="rounded-full px-2 py-1 text-[11px] bg-secondary text-muted-foreground">Hidden</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEditBrand(b)} data-testid={`admin-brand-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => removeBrand(b)} data-testid={`admin-brand-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create / edit modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" data-testid="admin-brand-modal">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit brand" : "Add brand"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Brand name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. BMW" data-testid="admin-brand-name" />
            </div>
            <div>
              <Label>Logo</Label>
              <div className="mt-1 rounded-xl border border-dashed border-border p-4 bg-secondary/30">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="h-16 w-32 rounded-md bg-white border border-border flex items-center justify-center overflow-hidden">
                    {form.logo_url ? (
                      <img src={form.logo_url} alt="Preview" className="max-h-14 max-w-[112px] object-contain" onError={(e) => { e.currentTarget.style.opacity = "0.35"; }} />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 w-full">
                    <label className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-sm cursor-pointer hover:bg-secondary transition-colors">
                      <Upload className="h-4 w-4 text-[hsl(var(--primary))]" />
                      {uploading ? "Uploading…" : "Upload logo"}
                      <input
                        type="file"
                        accept="image/png,image/svg+xml,image/webp,image/jpeg"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => uploadLogo(e.target.files?.[0])}
                        data-testid="admin-brand-upload-input"
                      />
                    </label>
                    <p className="mt-2 text-[11px] text-muted-foreground">PNG (transparent) preferred. Auto-fits to max 120px height. Or paste a URL below.</p>
                    <Input className="mt-2" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="/api/uploads/brands/… or https://…" data-testid="admin-brand-logo-url" />
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Display order</Label>
                <Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} data-testid="admin-brand-order" />
                <p className="mt-1 text-[10px] text-muted-foreground">Lower = shown first.</p>
              </div>
              <div>
                <Label>Website (optional)</Label>
                <Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://…" data-testid="admin-brand-website" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3 bg-secondary/30">
              <div>
                <div className="font-medium text-sm">Show on homepage</div>
                <div className="text-[11px] text-muted-foreground">Hidden brands are kept but not displayed in the carousel.</div>
              </div>
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="h-5 w-5 accent-[hsl(var(--primary))]"
                data-testid="admin-brand-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={save} className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="admin-brand-save-btn">
              {editing ? "Save changes" : "Add brand"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
